import {
  DUPLICATE_EMAIL_MESSAGE,
  normalizeEmail,
  parentClaimOpen,
  type AuthAccount,
} from "./auth";

/**
 * Staff-only answer for "does this guardian email already have a login?"
 * The result never includes a password. Parents still hear {@link DUPLICATE_EMAIL_MESSAGE}.
 */
export type ResolvedLogin = {
  role: "parent" | "student" | "staff";
  createdAt: string | null;
  lastSignInAt: string | null;
  /** Imported parent still confirming children or replacing the one-time password. */
  claimOpen: boolean;
  active: boolean;
};

export type GuardianLoginLookup =
  | { kind: "blank" }
  | { kind: "none"; students: string[] }
  | { kind: "parent"; login: ResolvedLogin; students: string[] }
  | { kind: "other"; login: ResolvedLogin; students: string[] };

export type GuardianLoginSummary = {
  title: string;
  detail: string;
};

type DirectoryAccount = Pick<
  AuthAccount,
  "email" | "role" | "mustResetPassword" | "pendingStudentIds"
> & {
  createdAt?: string | null;
  lastSignInAt?: string | null;
  active?: boolean;
};

type NamedStudent = {
  given: string;
  family: string;
  guardianEmail: string;
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Calendar date in Leelanau. Missing or unreadable timestamps stay blank. */
export function formatLoginDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Detroit",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).formatToParts(date);
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  const year = parts.find((part) => part.type === "year")?.value;
  if (!month || !day || !year) return null;
  const known = MONTHS.find((item) => item === month);
  if (!known) return null;
  return `${known} ${day}, ${year}`;
}

export function guardianLoginError(message: string): string {
  if (/guardian_login_status|schema cache|could not find the function/i.test(message)) {
    return "Login lookup is not available on this database yet.";
  }
  return "Could not check that email.";
}

export function resolveLocalLogin(
  accounts: readonly DirectoryAccount[],
  email: string,
): ResolvedLogin | null {
  const key = normalizeEmail(email);
  if (!key) return null;
  const account = accounts.find((item) => normalizeEmail(item.email) === key);
  if (!account) return null;
  return {
    role: account.role,
    createdAt: timestamp(account.createdAt),
    lastSignInAt: timestamp(account.lastSignInAt),
    claimOpen: parentClaimOpen({
      role: account.role,
      mustResetPassword: account.mustResetPassword === true,
      pendingStudentIds: account.pendingStudentIds ?? [],
    }),
    active: account.active !== false,
  };
}

export function resolvedLoginFromRow(value: unknown): ResolvedLogin | null {
  if (!isRecord(value)) return null;
  const role = loginRole(value.role);
  if (!role) return null;
  const claimOpen = value.claim_open === true || value.must_reset_password === true;
  return {
    role,
    createdAt: timestamp(value.created_at),
    lastSignInAt: timestamp(value.last_sign_in_at),
    claimOpen: role === "parent" && claimOpen,
    active: value.status !== "inactive",
  };
}

export function describeGuardianLogin(
  login: ResolvedLogin | null,
  students: readonly NamedStudent[],
  email: string,
): GuardianLoginLookup {
  const key = normalizeEmail(email);
  if (!key || !key.includes("@")) return { kind: "blank" };
  const names = studentsOnFile(students, key);
  if (!login) return { kind: "none", students: names };
  if (login.role === "parent") return { kind: "parent", login, students: names };
  return { kind: "other", login, students: names };
}

export function guardianLoginSummary(lookup: GuardianLoginLookup): GuardianLoginSummary {
  if (lookup.kind === "blank") {
    return {
      title: "No email to check",
      detail: "Enter the guardian email from the call or the student record.",
    };
  }
  const filed = onFile(lookup.students);
  if (lookup.kind === "none") {
    return {
      title: "No login yet",
      detail: filed
        ? `${filed} They still need to create a login with this email.`
        : "No student is on file for this email.",
    };
  }
  if (!lookup.login.active) {
    return {
      title: "Inactive login",
      detail: joinParts([
        roleExists(lookup.login.role),
        "The account is inactive. Password reset opens Lift after the account is active again.",
        filed,
      ]),
    };
  }
  if (lookup.kind === "parent" && lookup.login.claimOpen) {
    return {
      title: "Claim still open",
      detail: joinParts([
        "A parent login exists for this email.",
        "It is still on the claim screen. Ask them to sign in, confirm which children are theirs, and choose a password.",
        filed,
      ]),
    };
  }
  if (lookup.kind === "parent") {
    const created = formatLoginDate(lookup.login.createdAt);
    const seen = formatLoginDate(lookup.login.lastSignInAt);
    return {
      title: "Parent login",
      detail: joinParts([
        created ? `Created ${created}.` : null,
        seen ? `Last sign-in ${seen}.` : null,
        created || seen ? null : "A parent login exists for this email.",
        "Ask them to sign in, or send them to Forgot password.",
        filed,
      ]),
    };
  }
  const role = lookup.login.role === "staff" ? "staff" : "student";
  return {
    title: role === "staff" ? "Staff login" : "Student login",
    detail: joinParts([
      `This email is a ${role} login.`,
      `Signup will say: ${DUPLICATE_EMAIL_MESSAGE}`,
      "The family account needs a different email.",
      filed,
    ]),
  };
}

function studentsOnFile(students: readonly NamedStudent[], email: string): string[] {
  return students.flatMap((student) => {
    if (normalizeEmail(student.guardianEmail) !== email) return [];
    const name = `${student.given} ${student.family}`.trim();
    return [name || "A student"];
  });
}

function onFile(names: string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return `${names[0]} is on file under this email.`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are on file under this email.`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]} are on file under this email.`;
}

function roleExists(role: ResolvedLogin["role"]): string {
  if (role === "staff") return "A staff login exists for this email.";
  if (role === "student") return "A student login exists for this email.";
  return "A parent login exists for this email.";
}

function joinParts(parts: Array<string | null>): string {
  return parts.filter((part): part is string => Boolean(part)).join(" ");
}

function timestamp(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function loginRole(value: unknown): ResolvedLogin["role"] | null {
  if (typeof value !== "string") return null;
  const role = value.trim().toLowerCase();
  if (role === "parent") return "parent";
  if (role === "student") return "student";
  if (role === "staff" || role === "admin") return "staff";
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
